import { Box, Divider, Grid, Link, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { MoneyWithStringCurrency } from 'modules/project_management/lib/formatText'
import { IProjectSpendingPanelData } from 'modules/project_management/pages/ProjectDetailsPage/tabs/SpendingPage/types'

export interface IProjectSpendingDetailSidepanel {
  onClose: () => void
  projectSpendingPanelData: IProjectSpendingPanelData
  showSpendingDetailPanel: boolean
}

const ProjectSpendingDetailSidepanel = ({
  onClose,
  projectSpendingPanelData,
  showSpendingDetailPanel,
}: IProjectSpendingDetailSidepanel) => {
  const { navigation } = useNavigation()
  const {
    effectiveOn,
    employeeName,
    exchangeRate,
    expensableId,
    legalEntityLegalName,
    legalEntityAmountCents,
    legalEntityAmountCurrency,
    originalAmountCents,
    originalAmountCurrency,
  } = projectSpendingPanelData

  const {
    access: { locale: accessLocale },
  } = useCurrentSession()

  const differentCurrency = originalAmountCurrency !== legalEntityAmountCurrency

  const LabelValue = ({ label, value }: { label: string; value: string }) => (
    <Box gap="s12" gridColSpan={{ xxs: 6 }}>
      <Text.Header3>{label}</Text.Header3>
      <Text.Normal color="grey1000">{value}</Text.Normal>
    </Box>
  )

  const formattedExchangeRate = (+(exchangeRate || 1).toFixed(2) * 100) | 0

  const formatedRate =
    legalEntityAmountCurrency &&
    MoneyWithStringCurrency(formattedExchangeRate, legalEntityAmountCurrency, accessLocale)

  return (
    <Sidepanel
      title={i18n.t('project_management.spending.spending_tab')}
      open={showSpendingDetailPanel}
      width="wide"
      onClose={onClose}
    >
      <Box gap="s24">
        <Box gap="s16">
          <Text.Header3>{i18n.t('common.employee')}</Text.Header3>
          <Text color="grey1000" size="300" weight="medium">
            {employeeName}
          </Text>
        </Box>
        <Box gap="s16">
          <Text.Header3>{i18n.t('common.legal_entity')}</Text.Header3>
          <Text color="grey1000" size="300" weight="medium">
            {legalEntityLegalName}
          </Text>
        </Box>

        <Divider />

        <Box gap="s16">
          <Text color="grey1000" size="300" weight="bold">
            {i18n.t('project_management.spending.table.side_panel.source')}
          </Text>
          <Link.Router
            to={navigation.expenses.list.expenses.expenseId.fullPath({ expense_id: expensableId })}
            external
          >
            {i18n.t('project_management.spending.table.side_panel.go_to_spending_source')}
          </Link.Router>
        </Box>

        <Divider />

        {!!legalEntityAmountCents && legalEntityAmountCurrency && differentCurrency && (
          <Box gap="s24">
            <Box gap="s16">
              <Text color="grey1000" size="300" weight="bold">
                {i18n.t('project_management.spending.table.side_panel.currency_conversion')}
              </Text>
              <Text.Normal>
                {i18n.t('project_management.spending.table.side_panel.conversion_explanation')}
              </Text.Normal>
            </Box>

            <Grid variant="fixed">
              <LabelValue
                label={i18n.t('common.rate')}
                value={`${MoneyWithStringCurrency(
                  100,
                  originalAmountCurrency,
                  accessLocale
                )} = ${formatedRate}`}
              />
              <LabelValue
                label={i18n.t('common.date_date')}
                value={dateBackendDateTimeToUI(effectiveOn)}
              />
            </Grid>

            <Grid variant="fixed">
              <LabelValue
                label={i18n.t('project_management.spending.table.side_panel.original_amount')}
                value={MoneyWithStringCurrency(
                  originalAmountCents,
                  originalAmountCurrency,
                  accessLocale
                )}
              />
              <LabelValue
                label={i18n.t('project_management.spending.table.side_panel.converted_amount')}
                value={MoneyWithStringCurrency(
                  legalEntityAmountCents,
                  legalEntityAmountCurrency,
                  accessLocale
                )}
              />
            </Grid>
          </Box>
        )}
      </Box>
    </Sidepanel>
  )
}

export default observer(ProjectSpendingDetailSidepanel)
